import { api } from "@/lib/axios";
import type {
  ApiResponse,
  CustomAddressDto,
  ReferenceAddress,
  ReqDeleteCustomAddress,
  ReqUpsertCustomAddress,
} from "../types/customAddress.types";

const freshGetConfig = (params?: Record<string, string>) => ({
  params: { ...params, _t: Date.now() },
  headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
});

export const listCustomAddresses = async (keyword: string): Promise<CustomAddressDto[]> => {
  const trimmed = keyword.trim();
  const { data } = await api.get<ApiResponse<CustomAddressDto[]>>(
    "/api/custom-address/admin/list",
    freshGetConfig(trimmed ? { keyword: trimmed } : undefined),
  );
  if (data.code !== 200) throw new Error(data.message);
  return data.result ?? [];
};

export const getCustomAddress = async (caIdx: number): Promise<CustomAddressDto | null> => {
  const { data } = await api.get<ApiResponse<CustomAddressDto | null>>(
    `/api/custom-address/admin/${caIdx}`,
    freshGetConfig(),
  );
  if (data.code !== 200) throw new Error(data.message);
  return data.result;
};

export const upsertCustomAddress = async (req: ReqUpsertCustomAddress): Promise<number> => {
  const { data } = await api.post<ApiResponse<number>>("/api/custom-address/admin/upsert", req);
  if (data.code !== 200) throw new Error(data.message);
  return data.result;
};

export const deleteCustomAddress = async (req: ReqDeleteCustomAddress): Promise<void> => {
  const { data } = await api.post<ApiResponse<null>>("/api/custom-address/admin/delete", req);
  if (data.code !== 200) throw new Error(data.message);
};

interface YlpAddressSearched {
  localName: string;
  lat: string;
  lng: string;
  source?: string;
}

/**
 * 기준 주소 검색 — 지도 중심을 등록 지점 근처로 옮기기 위한 용도.
 * 보완 주소 자체(source=CUSTOM)는 제외해 "기준"이 항상 실제 검색 결과가 되게 한다.
 */
export const searchReferenceAddress = async (keyword: string): Promise<ReferenceAddress[]> => {
  const trimmed = keyword.trim();
  if (!trimmed) return [];
  const { data } = await api.get<ApiResponse<{ addresses: YlpAddressSearched[] }>>(
    "/api/ylp/searchaddress",
    { params: { searchAddr: trimmed } },
  );
  if (data.code !== 200) throw new Error(data.message);
  const list = data.result?.addresses ?? [];
  const results: ReferenceAddress[] = [];
  for (const addr of list) {
    if (addr.source === "CUSTOM") continue;
    const lat = parseFloat(addr.lat);
    const lng = parseFloat(addr.lng);
    if (!addr.localName || !Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) continue;
    results.push({ label: addr.localName, lat, lng });
  }
  return results;
};
